import { apiAsync } from '../common/helpers'
import { PAID_LEAVE_TYPES } from '../constants/paidLeaveTypes'
import * as httpClient from '../clients/httpClient'
import timingUtility from '../utilities/timingUtility'

const errorMessages = {
  ERROR_RETRIEVING_LOCATIONS: 'error retrieving locations',
  NO_LOCATION_DATA: 'no data in get-locations lambda response'
}
const SORT_MAP = {
  displayName: 'displayName',
  locationName: 'displayName',
  state: 'address',
  address: 'address'
}

const getLocationDetails = async ({ mongoId }) => {
  return httpClient.getJsonResponse({
    path: `/external/locations/${mongoId}/details`,
    queryParameters: { products: 'minimumWage' }
  })
}

const getLocations = ({
  companyId,
  pageNumber,
  pageSize,
  products,
  search,
  sort,
  sortOrder,
  filterStates
}) => {
  return apiAsync({
    method: 'GET',
    api: `external/companies/${companyId}/locations`,
    params: {
      includeProductMap: true,
      includeProperties: 'legacy',
      pageNumber,
      pageSize,
      respectOverrides: true,
      products: products.join(','),
      search,
      sort,
      sortOrder,
      filterStates
    }
  })
}

const getPostingsLocations = ({
  companyId,
  pageNumber,
  pageSize,
  search,
  sort,
  sortOrder,
  filterStates
}) => {
  return apiAsync({
    method: 'GET',
    api: `external/companies/${companyId}/locations`,
    params: {
      pageNumber,
      pageSize,
      respectOverrides: true,
      products: 'postings',
      search,
      sort,
      sortOrder,
      filterStates
    }
  })
}

const getPaidLeaveLocations = ({
  companyId,
  pageNumber,
  pageSize,
  search,
  sort,
  sortOrder,
  filterStates
}) => {
  return getLocations({
    companyId,
    pageNumber,
    pageSize,
    products: PAID_LEAVE_TYPES,
    search,
    sort,
    sortOrder,
    filterStates
  })
}

const getAllLocationsFromSearch = async (searchText, companyId, products) => {
  const params = {
    pageNumber: 1,
    pageSize: 100000,
    searchText,
    products
  }

  return apiAsync({
    api: `external/companies/${companyId}/locations`,
    method: 'GET',
    params
  })
}

const getAllGeoLocations = async ({
  companyId,
  searchText,
  sort = 'displayName',
  sortOrder = 'asc',
  stateCodeFilter,
  products,
  includeProductMap,
  includeProperties
}) => {
  /**
   * note: this page size value is constrained by API Gateway size limitations
   * the limitation will get worse over time as additional wage updates are entered into the system.
   * a real fix will be to not load the dang world
   */
  const LOCATIONS_PAGE_SIZE = 300

  const baseLocationsResult = await getGeoLocations({
    companyId,
    products,
    searchText,
    sort,
    sortOrder,
    stateCodeFilter,
    pageNumber: 1,
    pageSize: LOCATIONS_PAGE_SIZE,
    includeProductMap,
    includeProperties
  })

  validateGetLocationsResult(baseLocationsResult)

  const locationsResultData = baseLocationsResult.body.data

  if (!locationsResultData) throw new Error(errorMessages.NO_LOCATION_DATA)

  return getRemainingGeoLocations({
    previousResult: locationsResultData.locations,
    total: locationsResultData && locationsResultData.total,
    pageSize: LOCATIONS_PAGE_SIZE,
    currentPage: 2,
    companyId,
    products,
    searchText,
    sort,
    sortOrder,
    stateCodeFilter,
    includeProductMap,
    includeProperties
  })
}

const getGeoLocations = async ({
  companyId,
  pageNumber,
  pageSize,
  products,
  searchText,
  sort,
  sortOrder,
  stateCodeFilter,
  includeProductMap,
  includeProperties,
  continueToken,
  retryNumber = 0
}) => {
  const GET_LOCATIONS_RETRIES = 3
  const GET_LOCATIONS_RETRY_DELAY_MS = 1000

  const queryParameters = {
    includeProductMap,
    products,
    pageSize,
    pageNumber,
    respectOverrides: true
  }

  if (continueToken) queryParameters.continueToken = continueToken
  if (stateCodeFilter) queryParameters.filterStates = stateCodeFilter
  if (sort) queryParameters.sort = SORT_MAP[sort]
  if (sortOrder) queryParameters.sortOrder = sortOrder
  if (searchText) queryParameters.search = searchText
  if (includeProperties) queryParameters.includeProperties = includeProperties.join(',')

  let result = await httpClient.getJsonResponse({
    path: `/external/companies/${companyId}/locations`, // routes to ELC get-locations lambda
    method: 'GET',
    queryParameters
  })

  try {
    validateGetLocationsResult(result)
  } catch (validationError) {
    if (retryNumber < GET_LOCATIONS_RETRIES) {
      await timingUtility.waitForMs(GET_LOCATIONS_RETRY_DELAY_MS)
      result = await getGeoLocations({
        companyId,
        pageNumber,
        pageSize,
        products,
        searchText,
        sort,
        sortOrder,
        stateCodeFilter,
        includeProductMap,
        includeProperties,
        continueToken,
        retryNumber: retryNumber + 1
      })
    }
  }

  return result
}

const getRemainingGeoLocations = async ({
  previousResult,
  total,
  pageSize,
  currentPage,
  companyId,
  products,
  searchText,
  sort,
  sortOrder,
  stateCodeFilter,
  includeProductMap,
  includeProperties
}) => {
  const newResult = await getGeoLocations({
    companyId,
    pageNumber: currentPage,
    pageSize,
    products,
    searchText,
    sort,
    sortOrder,
    stateCodeFilter,
    includeProductMap,
    includeProperties
  })

  const resultingLocationsJson = newResult.body
  const resultingLocations = resultingLocationsJson && resultingLocationsJson.data && resultingLocationsJson.data.locations

  if (!resultingLocations) throw new Error(errorMessages.ERROR_RETRIEVING_LOCATIONS)

  if (!resultingLocations.length) console.debug('no more locations to retrieve, exiting recursion')
  if (!resultingLocations.length) return previousResult

  // recurse, each time getting the next page
  return getRemainingGeoLocations({
    previousResult: previousResult.concat(resultingLocations),
    total,
    pageSize,
    currentPage: currentPage + 1,
    companyId,
    products,
    searchText,
    sort,
    sortOrder,
    stateCodeFilter,
    includeProductMap,
    includeProperties
  })
}

const validateGetLocationsResult = (baseResult) => {
  if (baseResult.statusCode !== 200) {
    console.error('error validating get-locations result', baseResult)

    throw new Error(errorMessages.ERROR_RETRIEVING_LOCATIONS)
  }
}

module.exports = {
  getLocationDetails,
  getGeoLocations,
  getLocations,
  getPaidLeaveLocations,
  getPostingsLocations,
  getAllLocationsFromSearch,
  getAllGeoLocations
}
